import { useState, useRef } from 'react';
import { SidebarTrigger } from '../../components/ui/sidebar';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePipelineNodes, useBuildTree } from '../../hooks/usePipelineNodes';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Loader2, Settings, Plus, Search } from 'lucide-react';
import type { PipelineNode } from '../../services/pipelineNode.service';
import { pipelineNodeService } from '../../services/pipelineNode.service';
import { NodeTreeView } from '../../components/NodeTreeView';
import { AddModelDialog } from '../../components/AddModelDialog';
import type { ModelType } from '../../components/AddModelDialog';
import { ViewModeToggle } from '../../components/ViewModeToggle';
import type { ViewMode } from '../../components/ViewModeToggle';
import { getPipelineNodeIcon } from '../../components/PipelineNodeIcon';

function formatNumber(num?: number): string {
  if (num === undefined || num === null) return '-';
  return new Intl.NumberFormat('en-US').format(num);
}


function getRecordCount(node: PipelineNode): number | undefined {
  // Prefer shape.total_records, fallback to total_records
  return node.shape?.total_records ?? node.total_records;
}

function formatDate(dateString?: string): string {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } catch {
    return 'Invalid date';
  }
}

type NodeStatus = 'Error' | 'Needs Build' | 'OK';

function getNodeStatus(node: PipelineNode): NodeStatus {
  // If last_build_completed is null, always needs build
  if (!node.last_build_completed) {
    return 'Needs Build';
  }
  
  if (node.build_error) {
    return 'Error';
  }
  
  if (node.marked_stale_at) {
    const staleAt = new Date(node.marked_stale_at);
    const lastBuild = new Date(node.last_build_completed);
    
    // If marked_stale_at is later than last_build_completed, needs build
    if (staleAt > lastBuild) {
      return 'Needs Build';
    }
  }
  
  return 'OK';
}

function getStatusBadge(status: NodeStatus) {
  switch (status) {
    case 'Error':
      return (
        <Badge variant="outline" className="border-destructive text-destructive">
          Error
        </Badge>
      );
    case 'Needs Build':
      return (
        <Badge variant="outline" className="border-warning text-warning">
          Needs Build
        </Badge>
      );
    case 'OK':
      return (
        <Badge variant="outline" className="border-success text-success">
          OK
        </Badge>
      );
  }
}

export default function Nodes() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [addModelOpen, setAddModelOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const [committedSearch, setCommittedSearch] = useState('');
  const pageSize = 50;

  // Node types to exclude
  const excludedNodeTypes = ['REPORT', 'DATE_DIMENSION', 'DIMENSION', 'DESTINATION'];

  const { data, isLoading, isFetching, error } = usePipelineNodes({
    page,
    pageSize,
    filters: { node_type: { $nin: excludedNodeTypes } },
    search: committedSearch,
  });

  const { data: buildTreeData, isLoading: isTreeLoading, error: treeError } = useBuildTree({
    enabled: viewMode === 'tree',
  });

  const nodes = data?.records || [];
  const totalPages = data?.total_pages || 1;

  const handleConfigure = (nodeId: string) => {
    navigate(`/develop/modeling/${nodeId}`);
  };

  const handleTreeNodeClick = (pipelineNodeId: string) => {
    navigate(`/develop/modeling/${pipelineNodeId}`);
  };

  const handleCreateModel = async (options: { type: ModelType; name: string }) => {
    // Map the dialog type to the backend node_type and flat_file flag
    let nodeType: string;
    let flatFile: boolean | undefined;

    switch (options.type) {
      case 'SOURCE_TABLE':
        nodeType = 'SOURCE';
        flatFile = false;
        break;
      case 'SOURCE_FILE':
        nodeType = 'SOURCE';
        flatFile = true;
        break;
      case 'STAGING':
        nodeType = 'STAGING';
        break;
      case 'CUSTOM':
        nodeType = 'CUSTOM';
        break;
    }

    const newNode = await pipelineNodeService.create({
      name: options.name,
      label: options.name,
      node_type: nodeType,
      flat_file: flatFile,
    });
    // Invalidate the query cache to refresh the list
    queryClient.invalidateQueries({ queryKey: ['pipelineNodes'] });
    queryClient.invalidateQueries({ queryKey: ['buildTree'] });
    // Navigate to the new node's detail page
    navigate(`/develop/modeling/${newNode.id}`);
  };

  // Filter tree nodes to exclude certain types
  const filteredTreeNodes = buildTreeData?.nodes.filter(
    (node) => !excludedNodeTypes.includes(node.type)
  ) || [];

  // Filter edges to only include those between visible nodes
  const visibleNodeIds = new Set(filteredTreeNodes.map(n => n.id));
  const filteredTreeEdges = buildTreeData?.edges.filter(
    (edge) => visibleNodeIds.has(edge.from_id) && visibleNodeIds.has(edge.to_id)
  ) || [];

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1">
            <SidebarTrigger className="md:hidden h-5 w-5" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/develop">Develop</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Modeling</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          {/* Page Title */}
          <h1 className="text-xl font-semibold text-foreground">Modeling</h1>
        </div>
        {/* Search, View Toggle and Add Model Button */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchRef}
              type="text"
              placeholder="Search models..."
              defaultValue=""
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const val = searchRef.current?.value ?? '';
                  setPage(1);
                  setCommittedSearch(val);
                }
              }}
              onBlur={() => {
                const val = searchRef.current?.value ?? '';
                if (val !== committedSearch) {
                  setPage(1);
                  setCommittedSearch(val);
                }
              }}
              className="w-64 pl-9"
            />
          </div>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
          <Button
            onClick={() => setAddModelOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Model
          </Button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'table' ? (
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Loading nodes...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-destructive text-sm">
                Failed to load nodes. Please try again.
              </p>
            </div>
          ) : nodes.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">No nodes found.</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[44%]">Label</TableHead>
                      <TableHead className="w-[10%]">Records</TableHead>
                      <TableHead className="w-[21%]">Last Build Time</TableHead>
                      <TableHead className="w-[15%]">Status</TableHead>
                      <TableHead className="w-[10%] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nodes.map((node: PipelineNode) => (
                      <TableRow key={node.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {getPipelineNodeIcon(node.node_type)}
                            <div className="flex flex-col">
                              <span>{node.label || node.name || 'Unnamed Node'}</span>
                              {node.name && node.name !== node.label && (
                                <span className="text-xs text-muted-foreground font-normal">
                                  {node.name}
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatNumber(getRecordCount(node))}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(node.last_build_completed)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(getNodeStatus(node))}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleConfigure(node.id)}
                            title="Configure"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (page > 1) setPage(page - 1);
                          }}
                          style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
                        />
                      </PaginationItem>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= page - 1 && pageNum <= page + 1)
                        ) {
                          return (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setPage(pageNum);
                                }}
                                isActive={pageNum === page}
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (pageNum === page - 2 || pageNum === page + 2) {
                          return (
                            <PaginationItem key={`ellipsis-${pageNum}`}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          );
                        }
                        return null;
                      })}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (page < totalPages) setPage(page + 1);
                          }}
                          style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          {isTreeLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Loading tree...</p>
              </div>
            </div>
          ) : treeError ? (
            <div className="p-8">
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-destructive text-sm">
                  Failed to load tree view. Please try again.
                </p>
              </div>
            </div>
          ) : filteredTreeNodes.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">No nodes found.</p>
            </div>
          ) : (
            <NodeTreeView
              nodes={filteredTreeNodes}
              edges={filteredTreeEdges}
              onNodeClick={handleTreeNodeClick}
              className="h-full"
            />
          )}
        </div>
      )}

      <AddModelDialog
        open={addModelOpen}
        onOpenChange={setAddModelOpen}
        onCreate={handleCreateModel}
      />
    </div>
  );
}

